import { Button } from '@forwardflow/ui-v2'
import { useState, useEffect } from 'react'
import { useOrganizationDetails } from '@forwardflow/queries/organization.queries'
import { IBusinessDetails } from '@forwardflow/types/onboarding'
import { OrganizationDetailsForm } from '@forwardflow/modules/settings/OrgDetails/OrganizationDetailsForm'
import OrganizationService from '@forwardflow/services/OrganizationService'
import { message } from 'antd'

const OrganizationDetails = () => {
   const [isEditing, setIsEditing] = useState(false)
   const { data, mutate } = useOrganizationDetails({})
   const [formData, setFormData] = useState<IBusinessDetails | null>(null)

   // Update formData when data changes
   useEffect(() => {
      if (data?.businessDetails) {
         setFormData(data.businessDetails as unknown as IBusinessDetails)
      }
   }, [data])

   const handleEdit = () => {
      setIsEditing(true)
      // Initialize form data with current data
      if (data?.businessDetails) {
         setFormData(data.businessDetails as unknown as IBusinessDetails)
      }
   }

   const handleCancel = () => {
      setIsEditing(false)
      // Reset form data to original
      if (data?.businessDetails) {
         setFormData(data.businessDetails as unknown as IBusinessDetails)
      }
      mutate()
   }

   const handleSave = async () => {
      try {
         if (!formData) {
            message.error('No data to save')
            return
         }
         
         // Filter to only include fields supported by the API
         const apiPayload: Partial<IBusinessDetails> = {
            city: formData.city,
            email: formData.email,
            state: formData.state,
            street: formData.street,
            hasTeam: formData.hasTeam,
            postalCode: formData.postalCode,
            websiteUrl: formData.websiteUrl,
            phoneNumber: formData.phoneNumber,
            businessEIIN: formData.businessEIIN,
            businessType: formData.businessType,
            businessCname: formData.businessCname,
            businessTeamName: formData.businessTeamName,
            businessOwnerName: formData.businessOwnerName,
            legalBusinessName: formData.legalBusinessName,
            businessOwnerTitle: formData.businessOwnerTitle,
            friendlyBusinessName: formData.friendlyBusinessName,
            introductionTemplate: formData.introductionTemplate,
            businessBrokerageName: formData.businessBrokerageName,
         }
         
         await OrganizationService.updateBusinessDetails(apiPayload)
         message.success('Business details updated successfully')
         setIsEditing(false)
         mutate() // Refresh the data
      } catch (error) {
         message.error('Failed to update business details')
      }
   }

   const handleFormChange = (values: Partial<IBusinessDetails>) => {
      setFormData((prev) => ({
         ...prev,
         ...values,
      } as IBusinessDetails))
   }

   return (
      <div className="flex flex-col flex-1 h-full overflow-hidden">
         <div className="flex items-center justify-between h-20 pl-8 pr-4 bg-white/5 shrink-0">
            <span className="text-lg font-medium">Organization Details</span>
            {!isEditing ? (
               <Button
                  onClick={handleEdit}
                  variant="secondary"
                  className="!h-10 !rounded !mt-0 flex items-center gap-4 max-w-32"
               >
                  Edit
               </Button>
            ) : (
               <div className="flex gap-3">
                  <Button
                     onClick={handleCancel}
                     className="!h-10 !rounded px-7 !mt-0 max-w-40 bg-white/10 border-primary/10 hover:border-primary/10 hover:bg-white/20"
                  >
                     Cancel
                  </Button>
                  <Button
                     onClick={handleSave}
                     className="!h-10 !rounded px-7 !mt-0 max-w-40"
                  >
                     Save
                  </Button>
               </div>
            )}
         </div>
         <div className="flex-1 overflow-y-auto">
            <OrganizationDetailsForm
               businessDetails={formData || (data?.businessDetails as unknown as IBusinessDetails) || null}
               disabled={!isEditing}
               onChange={handleFormChange}
            />
         </div>
      </div>
   )
}

export default OrganizationDetails
